import { BootstrapUI, bootstrapUI, ElementOptions } from '../bootstrapui';
import { BaseUITools } from '../baseUITools';
import { getI18nText, toStandardHexColor } from '@/utils/commonFunc';
import { globalConfig } from '@/utils/globalConfig';

import '@simonwep/pickr/dist/themes/nano.min.css';
import '@/uiControls/styles/components.scss';
import Pickr from '@simonwep/pickr';

export interface ColorSelectorOptions extends ElementOptions {
    /** 标签文本 */
    label?: string;
    /** 是否显示标签 */
    showLabel?: boolean;
    /** 是否显示当前值 */
    showValue?: boolean;
    /** 初始颜色值 */
    color?: string;
    /** 国际化key */
    i18nKey?: string;
    containerClass?: string;
    labelClass?: string;
    valueClass?: string;
}

export class ColorSelectorPickrUI extends BaseUITools {
    private currentColor: string;
    private pickrInstance!: Pickr;
    private colorPreviewElement!: HTMLElement;
    private colorValueElement!: HTMLInputElement;
    private options: ColorSelectorOptions;
    private onChangeCallbacks: ((color: string) => void)[] = [];
    private isUpdatingFromPickr: boolean = false; // 防止循环调用的标志

    constructor(options: ColorSelectorOptions = {}) {
        super('color-selector-container');
        this.options = {
            label: '颜色选择器',
            showLabel: true,
            showValue: true,
            containerClass: 'color-selector-container',
            labelClass: 'color-selector-label',
            valueClass: 'color-selector-value',
            ...options
        };
        this.currentColor = options.color ? toStandardHexColor(options.color) : globalConfig.lightSettings.defalutSettings.color;
        this.container = this.createElement();
        this.initializePickr();
        this.setupEventListeners();
        // 初始化显示值
        this.updateColor(this.currentColor, false);
    }

    private createElement(): HTMLElement {
        const container = this.ui.createElement({
            tag: 'div',
            className: ['vision-color-picker-wrapper', ...(this.options.containerClass ? [this.options.containerClass] : [])]
        });

        const pickerElement = this.ui.createElement({
            tag: 'div',
            className: 'vision-color-picker-content'
        });
        if (this.options.showLabel) {
            const labelElement = this.ui.createElement({
                tag: 'div',
                className: [...(this.options.labelClass ? [this.options.labelClass] : [])],
                textContent: this.options.label || '',
                attributes: {
                    ...(this.options.i18nKey ? { 'data-i18n': this.options.i18nKey } : {})
                }
            });
            container.appendChild(labelElement);
        }
        const previewElement = this.ui.createElement({
            tag: 'div',
            className: ['vision-color-preview']
        });
        this.colorPreviewElement = previewElement;

        // 创建 Pickr 容器
        const pickrContainer = this.ui.createElement({
            tag: 'div',
            className: 'vision-color-picker-input'
        });
        previewElement.appendChild(pickrContainer);
        pickerElement.appendChild(previewElement);

        const valueElement = this.ui.createElement({
            tag: 'input',
            className: ['vision-color-value', 'input-control', ...(this.options.valueClass ? [this.options.valueClass] : [])],
            attributes: {
                type: 'text',
                value: this.currentColor
            }
        });
        this.colorValueElement = valueElement as HTMLInputElement;
        pickerElement.appendChild(valueElement);
        container.appendChild(pickerElement);

        return container;
    }

    private initializePickr(): void {
        const pickrContainer = this.colorPreviewElement.querySelector('.vision-color-picker-input') as HTMLElement;
        if (!pickrContainer) {
            console.error('Pickr container not found');
            return;
        }

        this.pickrInstance = Pickr.create({
            el: pickrContainer,
            theme: 'nano',
            default: this.currentColor,
            swatches: null,
            components: {
                preview: true,
                opacity: true,
                hue: true,
                interaction: {
                    hex: true,
                    rgba: true,
                    hsla: true,
                    hsva: false,
                    cmyk: false,
                    input: true,
                    clear: false,
                    save: true
                }
            }
        });

        // 监听 Pickr 的颜色变化事件
        this.pickrInstance.on('change', (color: any) => {
            if (color && !this.isUpdatingFromPickr) {
                const hexColor = color.toHEXA().toString();
                // 只在颜色真正改变时才更新
                if (hexColor !== this.currentColor) {
                    this.isUpdatingFromPickr = true;
                    this.updateColor(hexColor, true);
                    this.isUpdatingFromPickr = false;
                }
            }
        });

        // 监听 Pickr 的保存事件（用户点击保存按钮）
        this.pickrInstance.on('save', (color: any) => {
            if (color && !this.isUpdatingFromPickr) {
                const hexColor = color.toHEXA().toString();
                this.isUpdatingFromPickr = true;
                this.updateColor(hexColor, true);
                this.isUpdatingFromPickr = false;
                this.pickrInstance.hide();
            }
        });
    }

    private setupEventListeners(): void {

        // 监听颜色值输入框的输入事件，过滤和格式化输入
        this.colorValueElement.addEventListener('input', (event) => {
            const input = event.target as HTMLInputElement;
            let value = input.value;
            const cursorPosition = input.selectionStart || 0;

            // 保存 0x 前缀
            const hasPrefix = value.toLowerCase().startsWith('0x');
            let hexValue = hasPrefix ? value.slice(2) : value;

            // 只保留数字 0-9 和大写字母 A-F，小写自动转大写
            hexValue = hexValue.replace(/[^0-9A-Fa-f]/g, '').toUpperCase();

            // 限制长度为6个字符（标准颜色值 RRGGBB）
            hexValue = hexValue.slice(0, 6);

            // 如果没有 0x 前缀，自动添加
            const newValue = '0x' + hexValue;

            // 更新输入框的值
            input.value = newValue;

            // 计算新的光标位置：统计光标之前有多少个有效的十六进制字符
            const textBeforeCursor = hasPrefix ? value.slice(2, cursorPosition) : value.slice(0, cursorPosition);
            const validCharsBeforeCursor = textBeforeCursor.replace(/[^0-9A-Fa-f]/gi, '').length;
            // 新光标位置 = 0x (2个字符) + 有效字符数量
            const newCursorPosition = Math.min(2 + validCharsBeforeCursor, newValue.length);
            input.setSelectionRange(newCursorPosition, newCursorPosition);
        });

        // 监听颜色值输入框的回车事件
        this.colorValueElement.addEventListener('keydown', (event) => {
            if (event.key === 'Enter') {
                const inputValue = (event.target as HTMLInputElement).value.trim();
                // 将 0x 格式转换为 # 格式进行验证和更新
                const colorValue = inputValue.replace(/^0x/i, '#');
                // 验证是否为有效的十六进制颜色值 (#RRGGBB 或 #RRGGBBAA)
                if (this.isValidHexColor(colorValue)) {
                    this.updateColor(colorValue, true);
                } else {
                    // 如果输入无效，恢复为当前颜色值
                    // this.colorValueElement.value = this.currentColor;
                }
            }
        });

        // 监听失去焦点事件，如果输入无效，恢复为当前颜色值
        this.colorValueElement.addEventListener('blur', () => {
            const inputValue = this.colorValueElement.value.trim();
            // 将 0x 格式转换为 # 格式进行验证
            const colorValue = inputValue.replace(/^0x/i, '#');
            if (!this.isValidHexColor(colorValue)) {
                // 恢复为当前颜色值（转换为 0x 格式显示）
                const hexValue = this.currentColor.replace(/^#/, '');
                this.colorValueElement.value = '0x' + hexValue.toUpperCase();
            }
        });
    }

    /**
     * 验证是否为有效的十六进制颜色值
     * @param color 颜色值字符串
     * @returns 是否为有效的十六进制颜色值
     */
    private isValidHexColor(color: string): boolean {
        // 匹配 #RRGGBB 或 #RRGGBBAA 格式
        const hexColorPattern = /^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/;
        return hexColorPattern.test(color);
    }

    private updateColor(color: string, triggerCallbacks: boolean = false): void {
        // 规范化颜色值（确保是 #RRGGBB 格式）
        const normalizedColor = color.startsWith('#') ? color : '#' + color.replace(/^0x/i, '');

        // 如果颜色没有改变，直接返回
        if (normalizedColor === this.currentColor && !triggerCallbacks) {
            return;
        }

        this.currentColor = normalizedColor;

        // 更新 Pickr 实例的颜色（只在不是从 Pickr 触发时才更新，避免循环）
        if (this.pickrInstance && !this.isUpdatingFromPickr) {
            try {
                // 临时设置标志，防止 setColor 可能触发的事件导致循环
                this.isUpdatingFromPickr = true;
                this.pickrInstance.setColor(normalizedColor, false); // false 表示不触发 change 事件
                this.isUpdatingFromPickr = false;
            } catch (error) {
                console.error('Error setting Pickr color:', error);
                this.isUpdatingFromPickr = false;
            }
        }

        this.colorPreviewElement.style.backgroundColor = normalizedColor;
        // 将 # 格式转换为 0x 格式显示在输入框中
        const hexValue = normalizedColor.replace(/^#/, '');
        this.colorValueElement.value = '0x' + hexValue.toUpperCase();

        if (triggerCallbacks) {
            this.onChangeCallbacks.forEach(callback => {
                try {
                    callback(normalizedColor);
                } catch (error) {
                    console.error('Error in color change callback:', error);
                }
            });
        }
    }

    public setColor(color: string, triggerCallbacks: boolean = false): void {
        this.updateColor(color, triggerCallbacks);
    }

    public getColor(): string {
        return this.currentColor;
    }

    /**
     * 设置颜色选择器的禁用状态
     * @param disabled 如果为 true，则禁用颜色选择器；如果为 false，则启用
     */
    public setDisabled(disabled: boolean): void {
        if (this.pickrInstance) {
            if (disabled) {
                this.pickrInstance.disable();
            } else {
                this.pickrInstance.enable();
            }
        }
    }

    /**
     * 注册颜色改变时的回调函数
     * @param callback 颜色改变时调用的回调函数，参数为新颜色值
     * @returns 返回一个函数，调用该函数可以移除这个回调
     */
    public onChange(callback: (color: string) => void): () => void {
        this.onChangeCallbacks.push(callback);
        // 返回移除回调的函数
        return () => {
            const index = this.onChangeCallbacks.indexOf(callback);
            if (index > -1) {
                this.onChangeCallbacks.splice(index, 1);
            }
        };
    }

    /**
     * 移除所有颜色改变的回调函数
     */
    public removeAllCallbacks(): void {
        this.onChangeCallbacks = [];
    }

    /**
     * 获取容器元素
     */
    public getElement(): HTMLElement {
        return this.container;
    }

    /**
     * 销毁 Pickr 实例
     */
    public destroy(): void {
        if (this.pickrInstance) {
            this.pickrInstance.destroy();
        }
    }
}